import { Star, AlertTriangle, ArrowRight, BookOpen, Lightbulb, FileText, Printer, MessageCircleQuestion } from"lucide-react";
import { useEffect } from"react";
import { PCG_INDEX, NATURE_LABELS, SENS_LABELS, formatEUR } from"@/lib/pcgUtils";
import { getClass } from"@/data/classMeta";
import { getFaqsForAccount } from"@/lib/faqLinks";
import CopyEcritureButton from"@/components/CopyEcritureButton";

const Section = ({ icon: Icon, title, color, children }) => (
 <div className="bg-white border border-keyto rounded-xl shadow-keyto p-6">
 <div className="flex items-center gap-3 mb-4">
 <div
 className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
 style={{ backgroundColor: color +"15", color }}
 >
 <Icon size={18} />
 </div>
 <h3 className="font-heading text-base md:text-lg font-extrabold text-[#15294F]">
 {title}
 </h3>
 </div>
 {children}
 </div>
);

const Ecriture = ({ lignes }) => {
 const totalD = lignes.reduce((s, l) => s + (l.d || 0), 0);
 const totalK = lignes.reduce((s, l) => s + (l.k || 0), 0);
 return (
 <div className="bg-slate-50 border border-keyto rounded-lg overflow-hidden">
 <div className="grid grid-cols-12 bg-[#15294F] text-white text-xs font-semibold p-3 uppercase tracking-wider">
 <div className="col-span-2">Compte</div>
 <div className="col-span-6">Libellé</div>
 <div className="col-span-2 text-right">Débit</div>
 <div className="col-span-2 text-right">Crédit</div>
 </div>
 {lignes.map((l, i) => (
 <div
 key={i}
 className="grid grid-cols-12 p-3 border-b border-slate-200 last:border-b-0 font-mono text-sm items-center bg-white"
 >
 <div className="col-span-2 font-bold text-[#15294F]">{l.c}</div>
 <div className="col-span-6 font-sans text-slate-800">{l.l}</div>
 <div className="col-span-2 text-right text-emerald-700 font-bold">
 {formatEUR(l.d)}
 </div>
 <div className="col-span-2 text-right text-rose-700 font-bold">
 {formatEUR(l.k)}
 </div>
 </div>
 ))}
 <div className="grid grid-cols-12 p-3 bg-slate-100 border-t border-slate-300 font-mono text-sm font-bold">
 <div className="col-span-8 uppercase tracking-wider text-xs text-slate-600">
 Total équilibré
 </div>
 <div className="col-span-2 text-right text-[#15294F]">{formatEUR(totalD)}</div>
 <div className="col-span-2 text-right text-[#15294F]">{formatEUR(totalK)}</div>
 </div>
 </div>
 );
};

export default function AccountDetail({ account, isFav, onToggleFav, onSelect, onView, onOpenFAQ }) {
 useEffect(() => {
 if (account && onView) onView(account.num);
 }, [account, onView]);

 if (!account) return null;

 const cls = getClass(account.classe);
 const nature = NATURE_LABELS[account.nature] || NATURE_LABELS.mixte;
 const sens = SENS_LABELS[account.sens] || SENS_LABELS.M;

 return (
 <div data-testid={`account-detail-${account.num}`} className="space-y-6">
 {/* Bandeau "en cours de révision EC" — visible tant que la fiche n'est
     pas contresignée par l'expert-comptable (LOT 1 Step 1.7). */}
 {account.to_review && !account.verified_ec && (
  <div
   data-testid="review-banner"
   className="rounded-md bg-sky-50 border border-sky-200 px-3 py-2 text-[11px] md:text-xs text-sky-900 leading-relaxed"
  >
   <strong className="font-bold">Fiche en cours de révision par un expert-comptable.</strong>{" "}
   Cette fiche provient de l&apos;extraction automatique du PCG et n&apos;a pas encore
   été contresignée par notre équipe. Le contenu peut évoluer.
  </div>
 )}

 {/* Bandeau discret : site non officiel */}
 <div
 data-testid="unofficial-banner"
 className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] md:text-xs text-amber-900 leading-relaxed"
 >
 <strong className="font-bold">Site non officiel.</strong> Contenu pédagogique
 rédigé par le Cabinet KEYTO. Il ne se substitue pas au règlement ANC 2014-03
 modifié ni à un conseil comptable ou fiscal personnalisé.
 </div>

 {/* En-tête */}
 <div
 className="rounded-2xl border border-keyto shadow-keyto-lg p-6 md:p-8 bg-white"
 style={{ borderTop: `4px solid ${cls.color.primary}` }}
 >
 <div className="flex flex-wrap items-start justify-between gap-4">
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-2 mb-3 flex-wrap">
 <span
 className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full text-white"
 style={{ backgroundColor: cls.color.primary }}
 >
 Classe {cls.id} · {cls.label}
 </span>
 <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full border ${nature.color}`}>
 {nature.label}
 </span>
 <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full border ${sens.color}`}>
 Sens normal : {sens.label}
 </span>
 </div>
 <div className="flex items-baseline gap-4 flex-wrap">
 <span
 className="font-mono font-black text-5xl md:text-6xl tracking-tighter"
 style={{ color: cls.color.text }}
 >
 {account.num}
 </span>
 <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-[#15294F] tracking-tight">
 {account.lib}
 </h2>
 </div>
 </div>
 <div className="flex items-center gap-2 no-print">
 <button
 data-testid={`print-${account.num}`}
 onClick={() => window.print()}
 className="p-3 rounded-lg border bg-white border-keyto text-slate-500 hover:bg-slate-50 transition-all"
 aria-label="Imprimer cette fiche"
 title="Imprimer / exporter en PDF"
 >
 <Printer size={20} />
 </button>
 <button
 data-testid={`fav-toggle-${account.num}`}
 onClick={onToggleFav}
 className={`p-3 rounded-lg border transition-all ${
 isFav
 ?"bg-amber-50 border-amber-400 text-amber-700"
 :"bg-white border-keyto text-slate-500 hover:bg-slate-50"
 }`}
 aria-label="Ajouter aux favoris"
 >
 <Star size={20} fill={isFav ?"#F59E0B" :"none"} />
 </button>
 </div>
 </div>
 </div>

 {/* À quoi sert ce compte */}
 <Section icon={BookOpen} title="À quoi sert ce compte ?" color="#2D6BE0">
 <p className="text-base text-slate-800 leading-relaxed">{account.usage}</p>
 </Section>

 {/* Fonctionnement débit/crédit */}
 <div className="grid md:grid-cols-2 gap-6">
 <Section icon={ArrowRight} title="Quand est-il débité ?" color="#2F7A5C">
 <p className="text-sm text-slate-700 leading-relaxed">{account.fonctionnement.debit}</p>
 </Section>
 <Section icon={ArrowRight} title="Quand est-il crédité ?" color="#A33B4B">
 <p className="text-sm text-slate-700 leading-relaxed">{account.fonctionnement.credit}</p>
 </Section>
 </div>

 {/* Exemple chiffré — MASQUÉ sur toutes les classes (décision KEYTO,
     6 février 2026). Les données restent en base ; réactiver en
     remettant la condition classe 6/7 (ou true) ci-dessous. */}
 {false && account.exemple && (
 typeof account.exemple ==="string" ? (
 <Section icon={FileText} title="Exemple chiffré" color="#3B1E6E">
 <p className="text-slate-700 leading-relaxed whitespace-pre-line">{account.exemple}</p>
 </Section>
 ) : (
 <Section icon={FileText} title={`Exemple chiffré : ${account.exemple.titre ||""}`} color="#3B1E6E">
 {Array.isArray(account.exemple.lignes) ? (
 <>
 <Ecriture lignes={account.exemple.lignes} />
 <CopyEcritureButton
 lignes={account.exemple.lignes}
 titre={account.exemple.titre || account.lib}
 />
 </>
 ) : (
 <p className="text-slate-700 leading-relaxed">{String(account.exemple)}</p>
 )}
 </Section>
 )
 )}

 {/* Comparatifs liés (FAQ) */}
 {(() => {
 const relatedFaqs = getFaqsForAccount(account.num);
 if (!relatedFaqs.length || !onOpenFAQ) return null;
 return (
 <Section icon={MessageCircleQuestion} title="Comparatifs liés à ce compte" color="#A56A2E">
 <div className="space-y-2">
 {relatedFaqs.map((f) => (
 <button
 key={f.id}
 data-testid={`account-faq-link-${f.id}`}
 onClick={() => onOpenFAQ(f.id)}
 className="w-full text-left flex items-center justify-between gap-3 p-3 rounded-lg border border-keyto bg-white hover:bg-amber-50 hover:border-[#A56A2E]/40 transition group"
 >
 <div className="flex-1 min-w-0">
 <span className="inline-block text-[10px] font-bold uppercase tracking-wider rounded bg-slate-100 text-slate-600 px-1.5 py-0.5 mb-1">
 {f.cat}
 </span>
 <p className="text-sm font-bold text-[#15294F] leading-tight">{f.titre}</p>
 <p className="text-xs text-slate-500 mt-0.5">{f.contexte}</p>
 </div>
 <span className="shrink-0 text-xs font-bold uppercase tracking-wider text-[#A56A2E] group-hover:text-[#15294F]">
 Comparer →
 </span>
 </button>
 ))}
 </div>
 </Section>
 );
 })()}

 {/* Comptes liés */}
 {account.lies && account.lies.length > 0 && (
 <Section icon={Lightbulb} title="Comptes liés ou à ne pas confondre" color="#2D6BE0">
 <ul className="space-y-3">
 {account.lies.map((l, i) => {
 const linked = PCG_INDEX[l.n];
 return (
 <li key={i} className="flex gap-3 items-start">
 <button
 data-testid={`linked-${l.n}`}
 onClick={() => linked && onSelect(l.n)}
 disabled={!linked}
 className="font-mono font-bold text-xs bg-[#15294F] text-white px-2.5 py-1 rounded hover:bg-[#2D6BE0] transition-colors shrink-0 disabled:opacity-50"
 >
 {l.n}
 </button>
 <p className="text-sm text-slate-700 leading-relaxed">
 {linked && (
 <span className="font-semibold text-[#15294F]">{linked.lib}. </span>
 )}
 {l.note}
 </p>
 </li>
 );
 })}
 </ul>
 </Section>
 )}

 {/* Erreurs fréquentes */}
 {account.erreurs && account.erreurs.length > 0 && (
 <Section icon={AlertTriangle} title="Erreurs fréquentes à éviter" color="#B85724">
 <ul className="space-y-2">
 {account.erreurs.map((e, i) => (
 <li key={i} className="flex gap-3 items-start">
 <span className="shrink-0 w-5 h-5 rounded-full bg-orange-100 text-orange-700 text-xs font-bold flex items-center justify-center mt-0.5">
 !
 </span>
 <p className="text-sm text-slate-800 leading-relaxed">{e}</p>
 </li>
 ))}
 </ul>
 </Section>
 )}
 </div>
 );
}
